import { useCallback, useEffect, useReducer, useRef, useState, type ReactNode } from "react";

import { useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { PinGateModal } from "@/components/account/medical-vault/pin-gate-modal";
import { showToast } from "@/lib/ui/toast";
import {
  isVaultLockedError,
  isVaultUnlocked,
  markVaultUnlocked,
  reportVaultLocked,
} from "@/lib/vault-unlock";

// Source: legacy src/hooks/use-protected-action.ts (1:1 port).
// Reuses the SPA's existing PinGateModal (medical-vault) + vault-unlock
// state — both already ported. Changes vs legacy: the GET
// /api/account/vault-security fetch moved from useEffect+fetch to react-query
// (per SPA conventions); markVaultUnlocked receives the server-configured
// unlockMinutes (parity with ProtectedContent); and (2026-10) it fails
// CLOSED — legacy swallowed a failed status read and let the action through
// unprotected; now the action waits for the status, and a status it cannot
// read runs nothing (a toast says to try again).
//
// The server is the truth (2026-10): the medical-vault, wallet (saved cards)
// and billing-change endpoints answer 403 `vault_locked` without the unlock
// (apps/api lib/vault-unlock.ts requireVaultUnlocked). Two entry points:
//   - triggerProtected(fn) — the portal-side check BEFORE an action: asks for
//     the PIN when this tab holds no unlock, then runs `fn`;
//   - runProtected(call) — wraps the API call itself: when the server refuses
//     it with vault_locked (the unlock lapsed, was ended elsewhere, or this
//     tab's status was stale), the gate opens again and, once the PIN or a
//     vault passkey is accepted, `call` runs ONE more time. Dismissing the
//     gate rejects with VaultUnlockCancelledError — callers stay silent on it.
// The unlock it records is the same one ProtectedContent uses (the PIN check
// writes the server's pm_vault).

interface SecurityData {
  method: string;
  hasPin: boolean;
  pinLength: number;
  unlockMinutes: number;
  protectedFeatures: string[];
}

const VAULT_SECURITY_KEY = ["api", "/api/account/vault-security"] as const;
const fetchSecurity = () => apiFetch.get<SecurityData>("/api/account/vault-security");

/** The server's rule (apps/api lib/vault-unlock.ts vaultProtects), for any of `features`. */
function protects(data: SecurityData, features: readonly string[]): boolean {
  if (data.method === "none" || !data.hasPin) return false;
  // `sign_in` is a virtual entry the status adds (passkey sign-in), not a stored area.
  const stored = (data.protectedFeatures || []).filter((f) => f !== "sign_in");
  // An empty stored list with a PIN set means the medical vault.
  const effective = stored.length === 0 ? ["medical_vault"] : stored;
  return features.some((f) => effective.includes(f));
}

/** The PIN gate was dismissed: the protected call did not run. */
export class VaultUnlockCancelledError extends Error {
  constructor() {
    super("Vault unlock cancelled");
    this.name = "VaultUnlockCancelledError";
  }
}

export function isVaultUnlockCancelled(err: unknown): boolean {
  return err instanceof VaultUnlockCancelledError;
}

/**
 * withSavingToast (lib/ui/toast.ts) for a task that goes through
 * runProtected: a dismissed PIN gate closes the toast — nothing was sent —
 * instead of reporting an error. Still rethrows, so callers can tell.
 */
export async function withProtectedSavingToast<T>(
  task: () => Promise<T>,
  /** `error` may read the failure, to name a known refusal instead of the generic line. */
  messages: { saving: string; saved: string; error: string | ((err: unknown) => string) },
): Promise<T> {
  const handle = showToast(messages.saving, "loading");
  try {
    const result = await task();
    handle.update(messages.saved, "success");
    return result;
  } catch (err) {
    if (isVaultUnlockCancelled(err)) handle.dismiss();
    else
      handle.update(
        typeof messages.error === "function" ? messages.error(err) : messages.error,
        "error",
      );
    throw err;
  }
}

/** What the open gate is waiting to do: go on after the unlock, or give up. */
interface PendingAction {
  proceed: () => void;
  cancel: () => void;
}

/**
 * Is `feature` protected and this tab without an unlock? `null` until the
 * vault status is read; `true` when it cannot be read (closed). Re-evaluated
 * on every unlock / lock in this tab.
 */
export function useVaultFeatureLocked(feature: string): boolean | null {
  const query = useQuery({ queryKey: VAULT_SECURITY_KEY, queryFn: fetchSecurity });
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    window.addEventListener("vault-unlocked", rerender);
    window.addEventListener("vault-locked", rerender);
    return () => {
      window.removeEventListener("vault-unlocked", rerender);
      window.removeEventListener("vault-locked", rerender);
    };
  }, []);
  if (query.isError) return true;
  if (!query.data) return null;
  return protects(query.data, [feature]) && !isVaultUnlocked();
}

/**
 * Hook to gate an action behind PIN verification.
 *
 * `context` is the protected-feature key (or keys — the gate applies when the
 * account protects ANY of them) checked against the user's
 * `protectedFeatures` list (e.g. "billing", "wallet", "medical_vault") —
 * named `feature` in the legacy hook.
 *
 * Usage:
 * ```tsx
 * const { triggerProtected, runProtected, PinGateElement } = useProtectedAction("billing");
 *
 * const handleChangePlan = () => {
 *   triggerProtected(() => {
 *     // This only runs after PIN is verified (or if no protection is set)
 *     setShowPlanChangeModal(true);
 *   });
 * };
 * // …and the API call itself, so a server-side vault_locked asks again:
 * await runProtected(() => apiFetch.post("/api/account/billing/change-plan", body));
 *
 * return (
 *   <>
 *     <Button onClick={handleChangePlan}>Change Plan</Button>
 *     {PinGateElement}
 *   </>
 * );
 * ```
 */
export function useProtectedAction(context: string | readonly string[]): {
  triggerProtected: (fn: () => void) => void;
  runProtected: <T>(call: () => Promise<T>) => Promise<T>;
  PinGateElement: ReactNode;
} {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [showGate, setShowGate] = useState(false);
  const pendingRef = useRef<PendingAction | null>(null);
  const featureKey = typeof context === "string" ? context : context.join(",");

  const securityQuery = useQuery({ queryKey: VAULT_SECURITY_KEY, queryFn: fetchSecurity });
  const securityData = securityQuery.data ?? null;

  /** Open the gate for `pending`; a gate already open for something else gives that up. */
  const openGate = useCallback((pending: PendingAction) => {
    const previous = pendingRef.current;
    pendingRef.current = pending;
    previous?.cancel();
    setShowGate(true);
  }, []);

  // Unmounted with the gate open: whatever waited on it is given up, not left hanging.
  useEffect(
    () => () => {
      const pending = pendingRef.current;
      pendingRef.current = null;
      pending?.cancel();
    },
    [],
  );

  const triggerProtected = useCallback(
    (fn: () => void) => {
      const decide = (data: SecurityData) => {
        if (!protects(data, featureKey.split(",")) || isVaultUnlocked()) {
          fn();
          return;
        }
        openGate({ proceed: fn, cancel: () => {} });
      };
      // The common case: the status is in — decide in the same tick (so `fn`
      // still runs inside the click, e.g. for a popup).
      if (securityData) {
        decide(securityData);
        return;
      }
      // Not loaded yet, or the read failed: fetch it now; without it, nothing runs.
      queryClient
        .fetchQuery({ queryKey: VAULT_SECURITY_KEY, queryFn: fetchSecurity })
        .then(decide)
        .catch(() => {
          showToast(t`We couldn't check your vault protection. Try again.`, "error");
        });
    },
    [featureKey, openGate, queryClient, securityData, t],
  );

  const runProtected = useCallback(
    <T,>(call: () => Promise<T>): Promise<T> =>
      call().catch((err: unknown) => {
        if (!isVaultLockedError(err)) throw err;
        // The server holds no unlock for this browser: drop the tab's hint,
        // bring every gate back, refresh the status (PIN length, method) and
        // ask here — then try the call once more.
        reportVaultLocked();
        void queryClient.invalidateQueries({ queryKey: VAULT_SECURITY_KEY });
        return new Promise<T>((resolve, reject) => {
          openGate({
            proceed: () => {
              call().then(resolve, reject);
            },
            cancel: () => reject(new VaultUnlockCancelledError()),
          });
        });
      }),
    [openGate, queryClient],
  );

  const handleUnlock = useCallback(() => {
    markVaultUnlocked(securityData?.unlockMinutes);
    setShowGate(false);
    const pending = pendingRef.current;
    pendingRef.current = null;
    pending?.proceed();
  }, [securityData?.unlockMinutes]);

  const handleCancel = useCallback(() => {
    setShowGate(false);
    const pending = pendingRef.current;
    pendingRef.current = null;
    pending?.cancel();
  }, []);

  const PinGateElement: ReactNode = showGate ? (
    <PinGateModal
      open={showGate}
      onUnlock={handleUnlock}
      onCancel={handleCancel}
      pinLength={securityData?.pinLength || 0}
      hasBiometric={securityData?.method === "biometric"}
    />
  ) : null;

  return { triggerProtected, runProtected, PinGateElement };
}
